import React from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/Search';
import { Conversation } from '../types/Conversation';
import { Message } from '../types/Message';
import { ResponseApiType } from '../../../types/ResponseApiType';
import ApiProvider from '../../../../core/api/ApiProvider';
import { TranslationService } from '../../../../core/services';

type Props = {
  conversations: Conversation[];
  currentConversation?: Conversation;
  onSetCurrentConversation(element: Conversation): void;
};

const ConversationsList = (props: Props) => {
  const [searchValue, setSearchValue] = React.useState<string>('');

  const changeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const filterConversations = (conversation: Conversation) => {
    return (
      conversation.caseDetails.caseNumber.includes(searchValue) ||
      conversation.messageTitle.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()) ||
      conversation.notes.some((x) =>
        x.noteText.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())
      ) ||
      conversation.sendDateFormatted.includes(searchValue)
    );
  };

  const getMessageContent = (message: Message) => {
    return message.noteText.slice(0, 35);
  };

  const notifyCaseHandlerMsgRead = async (messageId: any, caseNo: any) => {
    await ApiProvider.post<ResponseApiType>('MessagesModule/NotifyCaseHandlerMsgRead', {
      messageId,
      caseNo,
    });
  };

  const handleChange = (element: Conversation) => {
    props.onSetCurrentConversation(element);
    const lastNote = element.notes[element.notes.length - 1];
    if (lastNote && !lastNote.readByDebtor) {
      notifyCaseHandlerMsgRead(element.messageId, element.caseNo);
      lastNote.readByDebtor = true;
    }
    props.onSetCurrentConversation(element);
  };

  if (!props.conversations || props.conversations.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">{TranslationService.translate('NoNewMsgs')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', p: 2 }}>
      <TextField
        size="small"
        fullWidth
        placeholder={TranslationService.translate('Search')}
        value={searchValue}
        onChange={changeSearchValue}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
      />

      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          px: 1.5,
          pb: 1,
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{ width: '25%', textTransform: 'uppercase', color: '#414042', fontWeight: 'bold' }}
        >
          {TranslationService.translate('Date')}
        </Typography>
        <Typography
          variant="caption"
          sx={{ flex: 1, textTransform: 'uppercase', color: '#414042', fontWeight: 'bold' }}
        >
          {TranslationService.translate('Description')}
        </Typography>
        <Typography
          variant="caption"
          sx={{ width: '25%', textTransform: 'uppercase', color: '#414042', fontWeight: 'bold' }}
        >
          {TranslationService.translate('CaseNr')}
        </Typography>
      </Box>

      <List
        disablePadding
        sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#bbb', borderRadius: 0 },
          '&::-webkit-scrollbar-track': { bgcolor: '#eee' },
        }}
      >
        {props.conversations.filter(filterConversations).map((element) => {
          const isActive =
            props.currentConversation?.messageId === element.messageId;
          const lastNote = element.notes[element.notes.length - 1];

          return (
            <ListItem key={element.messageId} disablePadding divider>
              <ListItemButton
                selected={isActive}
                onClick={() => handleChange(element)}
                sx={{
                  py: 1.5,
                  px: 1.5,
                  borderLeft: element.isRead ? '3px solid transparent' : '3px solid',
                  borderLeftColor: element.isRead ? 'transparent' : 'primary.main',
                  fontWeight: element.isRead ? 'normal' : 'bold',
                  '&.Mui-selected': { bgcolor: '#ebf5f4' },
                  '&:hover': { bgcolor: '#ebf5f4' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      width: { xs: 'auto', lg: '25%' },
                      flexShrink: 0,
                      fontWeight: 'inherit',
                    }}
                  >
                    {element.sendDateFormatted}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ fontWeight: 'inherit' }}
                    >
                      {element.messageTitle}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ display: 'block' }}
                    >
                      {lastNote && getMessageContent(lastNote)}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      width: { xs: 'auto', lg: '25%' },
                      flexShrink: 0,
                      fontWeight: 'inherit',
                    }}
                  >
                    {element.caseDetails.caseNumber}
                  </Typography>
                </Box>
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default ConversationsList;
